import Line from '../canvas/line'
import { IndicatorType } from '../constants/common'
import { TrixCalculation } from '../indicator-calculation/trix'
import { drawInVisibleArea } from '../utils/draw_in_visible_area'
import Indicator from './indicator'

interface Attrs {
  period: string
}

class Trix extends Indicator<Attrs> {
  static type = IndicatorType.Trix
  static label = 'TRIX'

  trixCalculation: TrixCalculation | null = null
  declare period: number

  set(values: Partial<Attrs>) {
    super.set(values)
    this.parsePeriodInt(values)
  }

  compute() {
    if (this.isComputeNecessary()) {
      this.trixCalculation = new TrixCalculation({
        quote: this.data,
        options: {
          period: this.period,
        },
      })
      this.trixCalculation.calculate()
      this.lastValue = this.trixCalculation.calculatedValues.trix.last() ?? null
    }
    const trix = this.trixCalculation?.calculatedValues.trix ?? []
    const { min, max } = trix.length > 0 ? this.computeVisibleMinMax(trix) : this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(this.fy(0)),
        y2: Math.round(this.fy(0)),
        strokeStyle: '#ff8787',
        dashLength: 3,
      },
      this.model
    ).render(context)
    context.set('strokeStyle', this.getChartLayoutSettings().IndicatorSettings.general.Colors.line)
    context.translate(0.5, 0.5)
    context.beginPath()
    drawInVisibleArea({
      quote: this.data,
      paneModel: this.model,
      leftOffset: this.leftOffset,
      width: this.width,
      drawBarCallback: (i: number, x: number) => {
        const value = this.trixCalculation?.calculatedValues.trix[i]
        if (value) context.lineTo(x, ~~this.fy(value))
      },
    })
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getValueLabelsAtIndex(index: number) {
    const dataIndex = this.data.barToDataIndex[index]
    return [
      {
        color: this.getChartLayoutSettings().IndicatorSettings.general.Colors.line!,
        text: this.getValueLabel(this.trixCalculation?.calculatedValues.trix[dataIndex]),
      },
    ]
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.period ?? 9,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [period],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `TRIX ${this.period}`
  }
}

export default Trix
